import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import "../styles/pompom-faq.css";

/* ── Category metadata ─────────────────────────────── */
interface CatMeta { color: string; bg: string; border: string; icon: React.ReactNode; }

const CAT_META: Record<string, CatMeta> = {
    "All": {
        color: "#1976d2", bg: "rgba(25,118,210,0.09)", border: "rgba(25,118,210,0.25)",
        icon: (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/>
                <rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/>
            </svg>
        ),
    },
    "Sizes": {
        color: "#1565c0", bg: "rgba(21,101,192,0.08)", border: "rgba(21,101,192,0.22)",
        icon: (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 6H3"/><path d="M21 12H3"/><path d="M21 18H3"/>
            </svg>
        ),
    },
    "Materials": {
        color: "#2e7d32", bg: "rgba(46,125,50,0.08)", border: "rgba(46,125,50,0.22)",
        icon: (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polygon points="12 2 2 7 12 12 22 7 12 2"/>
                <polyline points="2 17 12 22 22 17"/>
                <polyline points="2 12 12 17 22 12"/>
            </svg>
        ),
    },
    "MOQ": {
        color: "#6a1b9a", bg: "rgba(106,27,154,0.08)", border: "rgba(106,27,154,0.22)",
        icon: (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
            </svg>
        ),
    },
    "Shipping": {
        color: "#00695c", bg: "rgba(0,105,92,0.08)", border: "rgba(0,105,92,0.22)",
        icon: (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="1" y="3" width="15" height="13"/>
                <polygon points="16 8 20 8 23 11 23 16 16 16 16 8"/>
                <circle cx="5.5" cy="18.5" r="2.5"/>
                <circle cx="18.5" cy="18.5" r="2.5"/>
            </svg>
        ),
    },
    "Custom Orders": {
        color: "#e47911", bg: "rgba(228,121,17,0.08)", border: "rgba(228,121,17,0.22)",
        icon: (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="13.5" cy="6.5" r="3.5"/>
                <circle cx="17.5" cy="13.5" r="3.5"/>
                <circle cx="8.5" cy="13.5" r="3.5"/>
            </svg>
        ),
    },
    "Pricing": {
        color: "#f57f17", bg: "rgba(245,127,23,0.08)", border: "rgba(245,127,23,0.22)",
        icon: (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="12" y1="1" x2="12" y2="23"/>
                <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
            </svg>
        ),
    },
    "Safety & Certifications": {
        color: "#1b5e20", bg: "rgba(27,94,32,0.08)", border: "rgba(27,94,32,0.22)",
        icon: (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
            </svg>
        ),
    },
    "Payment": {
        color: "#283593", bg: "rgba(40,53,147,0.08)", border: "rgba(40,53,147,0.22)",
        icon: (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="1" y="4" width="22" height="16" rx="2" ry="2"/>
                <line x1="1" y1="10" x2="23" y2="10"/>
            </svg>
        ),
    },
};

const CATEGORIES = ["All", "Sizes", "Materials", "MOQ", "Shipping", "Custom Orders", "Pricing", "Safety & Certifications", "Payment"];

const HERO_STATS = [
    { value: "8",    label: "Standard Sizes" },
    { value: "200+", label: "Colors Available" },
    { value: "100",  label: "Min. Order (pcs)" },
    { value: "50+",  label: "Countries Shipped" },
    { value: "4",    label: "Yarn Materials" },
];

interface FaqItem {
    id: number;
    category: string;
    question: string;
    answer: React.ReactNode;
    schemaText: string;
}

const FAQ_ITEMS: FaqItem[] = [
    {
        id: 1, category: "Sizes",
        question: "What sizes do your handmade pom pom balls come in?",
        schemaText: "Our handmade pom pom balls are available in 8 standard sizes measured by diameter: Mini (1cm, 2cm) — ideal for jewelry and fine trimmings; Small (3cm, 4cm) — popular for garment trims, hats, and scarves; Medium (5cm, 6cm) — best for home décor and wreaths; Large (8cm, 10cm) — perfect for cushions and wall hangings. Custom sizes from 0.5cm up to 15cm are available for bulk orders of 500 pieces or more.",
        answer: (
            <>
                <p>Our handmade pom pom balls are available in <strong>8 standard sizes</strong> measured by diameter:</p>
                <ul>
                    <li><strong>Mini:</strong> 1 cm and 2 cm — ideal for jewelry, earrings, and fine trimmings</li>
                    <li><strong>Small:</strong> 3 cm and 4 cm — popular for garment trims, hats, and scarves</li>
                    <li><strong>Medium:</strong> 5 cm and 6 cm — best for home décor, wreaths, and gift wrapping</li>
                    <li><strong>Large:</strong> 8 cm and 10 cm — perfect for cushions, wall hangings, and display décor</li>
                </ul>
                <p>Custom sizes from <strong>0.5 cm up to 15 cm</strong> are available for bulk orders of 500 pieces or more.</p>
                <div className="pp-faq-highlight-box">All sizes are hand-crafted for consistent shape, fullness, and density — no machine cutting.</div>
            </>
        ),
    },
    {
        id: 2, category: "Materials",
        question: "What materials are used to make your pom pom balls?",
        schemaText: "We use three premium yarn types: Acrylic yarn — soft, durable, and colorfast, ideal for home décor and fashion accessories; Wool yarn — natural, warm, and premium texture, perfect for winter wear and luxury items; Cotton yarn — breathable, hypoallergenic, and skin-friendly, best for baby clothing and eco-friendly products. All materials are non-toxic, OEKO-TEX certified, and available in 200+ colors.",
        answer: (
            <>
                <p>We use <strong>three premium yarn types</strong>, each suited to different applications:</p>
                <ul>
                    <li><strong>Acrylic yarn</strong> — soft, durable, and colorfast. Ideal for home décor, fashion accessories, and year-round craft projects. Most affordable option.</li>
                    <li><strong>Wool yarn</strong> — natural, warm, and premium texture. Perfect for winter wear, luxury scarves, and high-end gift decoration.</li>
                    <li><strong>Cotton yarn</strong> — breathable, hypoallergenic, and skin-friendly. Best for baby clothing, eco-friendly products, and summer craft items.</li>
                    <li><strong>Blended yarn</strong> — Combinations of natural and synthetic, such as wool-acrylic or cotton-polyester blends, designed to balance softness, durability, and cost.</li>
                </ul>
                <p>All materials are <strong>non-toxic</strong>, OEKO-TEX certified, and available in <strong>200+ colors</strong>. We can also blend materials on request.</p>
                <div className="pp-faq-highlight-box">Not sure which material? Request a free sample pack before placing your bulk order.</div>
            </>
        ),
    },
    {
        id: 3, category: "MOQ",
        question: "What is the minimum order quantity (MOQ) for pom pom balls?",
        schemaText: "Our minimum order quantities: Standard order — 100 pieces per color per size; Mixed assortment — 500 pieces total; Custom color or size — 1,000 pieces minimum; Private label/packaging — 2,000 pieces minimum; Sample order — 10 to 20 pieces. Volume pricing breaks apply at 1,000 / 5,000 / 10,000 pieces.",
        answer: (
            <>
                <p>Our minimum order quantities are structured to suit different buyer needs:</p>
                <ul>
                    <li><strong>Standard order</strong> — 500 pieces per color, per size</li>
                    <li><strong>Mixed assortment</strong> — 5000 pieces total (any mix of colors and sizes)</li>
                    <li><strong>Custom color or custom size</strong> — minimum 5,000 pieces</li>
                    <li><strong>Private label / packaging</strong> — minimum 5,000 pieces</li>
                    <li><strong>Sample order</strong> — 10 to 20 pieces available before bulk commitment</li>
                </ul>
                <p>Volume pricing breaks apply at <strong>1,000 / 5,000 / 10,000 pieces</strong> with increasing discounts at each tier.</p>
                <div className="pp-faq-highlight-box">New customers can order a sample pack to test quality before committing to bulk quantities.</div>
            </>
        ),
    },
    {
        id: 4, category: "Shipping",
        question: "How long does shipping take and which countries do you ship to?",
        schemaText: "We ship to 50+ countries worldwide. Delivery timelines: India (domestic) — 3 to 5 business days; USA, UK, Canada, Australia — 7 to 14 business days (air freight); UAE, Gulf countries — 5 to 10 business days; Europe (EU) — 8 to 14 business days; Large bulk orders (10,000+ pieces) — sea freight in 25 to 35 days. All orders include shipment tracking. DDP shipping available for USA, UK, and EU.",
        answer: (
            <>
                <p>We ship to <strong>50+ countries worldwide</strong>. Delivery timelines by region:</p>
                <ul>
                    <li><strong>India (domestic)</strong> — 3 to 5 business days via courier</li>
                    <li><strong>USA, UK, Canada, Australia</strong> — 7 to 14 business days (air freight)</li>
                    <li><strong>UAE, Gulf countries</strong> — 5 to 10 business days</li>
                    <li><strong>Europe (EU)</strong> — 8 to 14 business days</li>
                    <li><strong>Large bulk orders (10,000+ pieces)</strong> — sea freight in 25 to 35 days at lower cost</li>
                </ul>
                <p>All orders include <strong>shipment tracking</strong>. We offer <strong>DDP (Delivered Duty Paid)</strong> shipping to the USA, UK, and EU — no hidden import fees for you.</p>
                <div className="pp-faq-highlight-box">Express shipping (3–5 days worldwide) is available for urgent orders at additional cost.</div>
            </>
        ),
    },
    {
        id: 5, category: "Pricing",
        question: "What is the wholesale price per piece for pom pom balls?",
        schemaText: "Pricing depends on size, material, and order quantity. Indicative rates for 3cm pom poms: Acrylic (standard) — ₹2 to ₹4 per piece (1,000+ pieces); Cotton yarn — ₹4 to ₹7 per piece; Wool yarn (premium) — ₹6 to ₹12 per piece. Volume discounts: 5,000+ pieces — 10% discount; 10,000+ pieces — 20% discount; 25,000+ pieces — custom quote.",
        answer: (
            <>
                <p>Pricing depends on size, material, and order quantity. Indicative rates for 3 cm pom poms:</p>
                <ul>
                    <li><strong>Acrylic (standard)</strong> — ₹2 to ₹4 per piece (1,000+ pieces)</li>
                    <li><strong>Cotton yarn</strong> — ₹4 to ₹7 per piece</li>
                    <li><strong>Wool yarn (premium)</strong> — ₹6 to ₹12 per piece</li>
                </ul>
                <p>Volume discount tiers:</p>
                <ul>
                    <li><strong>5,000+ pieces</strong> — 1% discount applied automatically</li>
                    <li><strong>10,000+ pieces</strong> — 2% discount applied automatically</li>
                    <li><strong>25,000+ pieces</strong> — custom quote with maximum savings</li>
                </ul>
                <div className="pp-faq-highlight-box">Contact us with your exact size, color, and quantity for a formal quotation within 24 hours.</div>
            </>
        ),
    },
    {
        id: 6, category: "Custom Orders",
        question: "Can I get custom colors and private label packaging?",
        schemaText: "Yes — full customization is available: Custom Pantone color matching from 1,000 pieces per color; Private label hang tags from 2,000 pieces; Custom packaging/poly bags with logo from 2,000 pieces; Custom size (non-standard) from 500 pieces; Color swatch approval is free before production begins. Custom dyeing lead time is 10 to 15 business days in addition to standard production time.",
        answer: (
            <>
                <p>Yes — full customization is available for qualifying order sizes:</p>
                <ul>
                    <li><strong>Custom Pantone color matching</strong> — from 1,000 pieces per color</li>
                    <li><strong>Private label hang tags</strong> — from 2,000 pieces</li>
                    <li><strong>Custom packaging / poly bags with logo</strong> — from 2,000 pieces</li>
                    <li><strong>Custom size (non-standard)</strong> — from 500 pieces</li>
                    <li><strong>Color swatch approval</strong> — free, provided before production begins</li>
                </ul>
                <p>Custom dyeing lead time is <strong>10 to 15 business days</strong> in addition to standard production time.</p>
                <div className="pp-faq-highlight-box">Send us your Pantone code or a physical color sample and we'll match it precisely.</div>
            </>
        ),
    },
    {
        id: 7, category: "Safety & Certifications",
        question: "Are your pom pom balls safe for children and baby products?",
        schemaText: "Yes. Our cotton yarn pom poms are certified for baby and children's products: EN71 (European toy safety standard) — certified compliant; ASTM F963 (US toy safety) — certified compliant; OEKO-TEX Standard 100 — no harmful substances in any dyes or fibers; all dyes are non-toxic and colorfast. Safety certificates are provided upon request. Acrylic pom poms are safe for children aged 3 years and above.",
        answer: (
            <>
                <p>Yes. Our <strong>cotton yarn pom poms</strong> are specifically certified for use in baby and children's products:</p>
                <ul>
                    <li><strong>EN71</strong> (European toy safety standard) — certified compliant</li>
                    <li><strong>ASTM F963</strong> (US toy safety) — certified compliant</li>
                    <li><strong>OEKO-TEX Standard 100</strong> — no harmful substances in any dyes or fibers</li>
                    <li>All dyes are <strong>non-toxic and colorfast</strong> — safe for skin contact</li>
                </ul>
                <p>Safety certificates and test reports are <strong>provided upon request</strong> for wholesale orders. Acrylic pom poms are safe for children aged 3 years and above (not suitable for infants due to choking hazard for sizes under 3 cm).</p>
                <div className="pp-faq-highlight-box">Request official test certificates when placing your order — provided free of charge.</div>
            </>
        ),
    },
    {
        id: 8, category: "Payment",
        question: "What payment methods and terms do you accept for bulk orders?",
        schemaText: "We support multiple payment options: Bank transfer — SWIFT (international), NEFT/RTGS (India); PayPal — for orders up to USD 5,000; Letter of Credit (LC) — accepted for large orders USD 10,000+; Currencies accepted: USD, EUR, GBP, and INR. Standard payment terms: New customers — 50% advance, 50% before shipment; Returning customers — Net 30 terms available after 3 successful orders.",
        answer: (
            <>
                <p>We support multiple payment options to make international ordering easy:</p>
                <ul>
                    <li><strong>Bank transfer</strong> — SWIFT (international), NEFT/RTGS (India)</li>
                    <li><strong>PayPal</strong> — for orders up to USD 5,000</li>
                    <li><strong>Letter of Credit (LC)</strong> — accepted for large orders USD 10,000+</li>
                    <li><strong>Currencies accepted:</strong> USD, EUR, GBP, and INR</li>
                </ul>
                <p>Standard payment terms:</p>
                <ul>
                    <li><strong>New customers</strong> — 50% advance, 50% before shipment</li>
                    <li><strong>Returning customers</strong> — Net 30 terms available after 3 successful orders</li>
                </ul>
                <div className="pp-faq-highlight-box">All transactions are secured. A proforma invoice is provided before any payment is required.</div>
            </>
        ),
    },
];

const FAQ_SCHEMA = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQ_ITEMS.map((item) => ({
        "@type": "Question",
        name: item.question,
        acceptedAnswer: { "@type": "Answer", text: item.schemaText },
    })),
};

const getCatCount = (cat: string) =>
    cat === "All" ? FAQ_ITEMS.length : FAQ_ITEMS.filter((f) => f.category === cat).length;

/* ── Component ──────────────────────────────────────── */
const PompomFaq = () => {
    const navigate = useNavigate();
    const [activeCategory, setActiveCategory] = useState("All");
    const [openId, setOpenId] = useState<number | null>(null);

    const filtered =
        activeCategory === "All"
            ? FAQ_ITEMS
            : FAQ_ITEMS.filter((f) => f.category === activeCategory);

    const toggle = (id: number) => setOpenId((prev) => (prev === id ? null : id));

    const selectCat = (cat: string) => { setActiveCategory(cat); setOpenId(null); };

    return (
        <main className="pp-faq-page">

            {/* JSON-LD */}
            <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(FAQ_SCHEMA) }} />

            {/* ══ HERO ══════════════════════════════════════════ */}
            <section className="pp-faq-hero">
                <div className="pp-faq-hero-bg" aria-hidden="true" />

                {/* Blurred blob decorations */}
                <div className="pp-faq-blob pp-faq-blob--1" aria-hidden="true" />
                <div className="pp-faq-blob pp-faq-blob--2" aria-hidden="true" />
                <div className="pp-faq-blob pp-faq-blob--3" aria-hidden="true" />

                {/* Dot grid */}
                <div className="pp-faq-hero-dots" aria-hidden="true">
                    {Array.from({ length: 30 }).map((_, i) => <span key={i} />)}
                </div>

                <div className="pp-faq-hero-inner">
                    <div className="pp-faq-hero-tag">
                        <span className="pp-faq-hero-tag-dot" aria-hidden="true" />
                        Pom Pom FAQ
                    </div>
                    <h1 className="pp-faq-hero-title">
                        Everything You Need<br />
                        to Know About{" "}
                        <span className="pp-faq-hero-accent">Pom Poms</span>
                    </h1>
                    <p className="pp-faq-hero-sub">
                        Sizes, materials, MOQ, shipping, and custom orders —<br className="pp-faq-hero-br" />
                        answered by Arte Craft India's manufacturing team.
                    </p>

                    {/* Stat cards */}
                    <div className="pp-faq-hero-stats">
                        {HERO_STATS.map((s) => (
                            <div key={s.label} className="pp-faq-hero-stat">
                                <div className="pp-faq-hero-stat-val">{s.value}</div>
                                <div className="pp-faq-hero-stat-lbl">{s.label}</div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Wave divider */}
                <div className="pp-faq-hero-wave" aria-hidden="true">
                    <svg viewBox="0 0 1440 72" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M0,36 C240,72 480,0 720,36 C960,72 1200,0 1440,36 L1440,72 L0,72 Z" fill="#f0f4f9" />
                    </svg>
                </div>
            </section>

            {/* ══ MOBILE FILTERS (hidden on desktop) ═══════════ */}
            <div className="pp-faq-mobile-filters" role="navigation" aria-label="Filter by category">
                <div className="pp-faq-mobile-filters-inner">
                    {CATEGORIES.map((cat) => (
                        <button
                            key={cat}
                            className={`pp-faq-mobile-tab${activeCategory === cat ? " active" : ""}`}
                            style={activeCategory === cat ? {
                                background: CAT_META[cat].color,
                                borderColor: "transparent",
                            } : {}}
                            aria-pressed={activeCategory === cat}
                            onClick={() => selectCat(cat)}
                        >
                            {cat}
                        </button>
                    ))}
                </div>
            </div>

            {/* ══ BODY: SIDEBAR + CONTENT ═══════════════════════ */}
            <div className="pp-faq-body">
                <div className="pp-faq-layout">

                    {/* ── Sidebar ── */}
                    <aside className="pp-faq-sidebar" aria-label="FAQ categories">
                        <p className="pp-faq-sidebar-label">Browse by Topic</p>

                        <nav className="pp-faq-sidebar-nav">
                            {CATEGORIES.map((cat) => {
                                const meta = CAT_META[cat];
                                const count = getCatCount(cat);
                                const isActive = activeCategory === cat;
                                return (
                                    <button
                                        key={cat}
                                        className={`pp-faq-sidebar-btn${isActive ? " active" : ""}`}
                                        style={isActive ? {
                                            background: meta.bg,
                                            borderColor: meta.border,
                                            color: meta.color,
                                        } : {}}
                                        onClick={() => selectCat(cat)}
                                        aria-pressed={isActive}
                                    >
                                        <span
                                            className="pp-faq-sidebar-icon"
                                            style={isActive ? { color: meta.color } : {}}
                                        >
                                            {meta.icon}
                                        </span>
                                        <span className="pp-faq-sidebar-cat">{cat}</span>
                                        <span
                                            className="pp-faq-sidebar-count"
                                            style={isActive ? {
                                                background: meta.color,
                                                color: "#fff",
                                            } : {}}
                                        >
                                            {count}
                                        </span>
                                    </button>
                                );
                            })}
                        </nav>

                        {/* Sidebar contact nudge */}
                        <hr className="pp-faq-sidebar-divider" aria-hidden="true" />
                        <div className="pp-faq-sidebar-card">
                            <div className="pp-faq-sidebar-card-icon" aria-hidden="true">
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                                </svg>
                            </div>
                            <p className="pp-faq-sidebar-card-text">Can't find your answer?</p>
                            <button className="pp-faq-sidebar-card-btn" onClick={() => navigate("/contact")}>
                                Ask Us Directly
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M5 12h14M12 5l7 7-7 7"/>
                                </svg>
                            </button>
                        </div>
                    </aside>

                    {/* ── FAQ Content ── */}
                    <div className="pp-faq-content">
                        <div className="pp-faq-content-head">
                            <p className="pp-faq-count">
                                <span>{filtered.length}</span>{" "}
                                question{filtered.length !== 1 ? "s" : ""}{" "}
                                {activeCategory !== "All" && (
                                    <span className="pp-faq-count-cat">in {activeCategory}</span>
                                )}
                            </p>
                        </div>

                        {filtered.length === 0 ? (
                            <p className="pp-faq-empty">No questions in this category yet.</p>
                        ) : (
                            <div className="pp-faq-list">
                                {filtered.map((item, idx) => {
                                    const isOpen = openId === item.id;
                                    const meta = CAT_META[item.category];
                                    const displayNum = String(idx + 1).padStart(2, "0");
                                    return (
                                        <div
                                            key={item.id}
                                            className={`pp-faq-item${isOpen ? " open" : ""}`}
                                            style={{
                                                "--cat-color":  meta.color,
                                                "--cat-bg":     meta.bg,
                                                "--cat-border": meta.border,
                                            } as React.CSSProperties}
                                        >
                                            {/* Ghost watermark number */}
                                            <span className="pp-faq-ghost-num" aria-hidden="true">
                                                {displayNum}
                                            </span>

                                            <button
                                                className="pp-faq-question"
                                                onClick={() => toggle(item.id)}
                                                aria-expanded={isOpen}
                                            >
                                                <span className="pp-faq-question-left">
                                                    <span className="pp-faq-question-num">{displayNum}</span>
                                                    <span className="pp-faq-question-text">{item.question}</span>
                                                </span>
                                                <span className="pp-faq-chevron" aria-hidden="true">
                                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                                        <polyline points="6 9 12 15 18 9" />
                                                    </svg>
                                                </span>
                                            </button>

                                            <div className="pp-faq-answer-wrap">
                                                <div className="pp-faq-answer">
                                                    <div className="pp-faq-answer-inner">
                                                        <span
                                                            className="pp-faq-cat-pill"
                                                            style={{ color: meta.color, background: meta.bg, borderColor: meta.border }}
                                                        >
                                                            <span className="pp-faq-cat-pill-icon">{meta.icon}</span>
                                                            {item.category}
                                                        </span>
                                                        <div className="pp-faq-answer-body">{item.answer}</div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>

                </div>
            </div>

            {/* ══ CTA ═══════════════════════════════════════════ */}
            <section className="pp-faq-cta">
                <div className="pp-faq-cta-bg" aria-hidden="true" />
                <div className="pp-faq-cta-blob pp-faq-cta-blob--1" aria-hidden="true" />
                <div className="pp-faq-cta-blob pp-faq-cta-blob--2" aria-hidden="true" />
                <div className="pp-faq-cta-inner">
                    <div className="pp-faq-cta-content">
                        <span className="pp-faq-cta-tag">Get in Touch</span>
                        <h2 className="pp-faq-cta-title">Ready to Place a Bulk Pom Pom Order?</h2>
                        <p className="pp-faq-cta-sub">
                            Share your size, colour, and quantity requirements — we'll send a custom quote within 24 hours.
                        </p>
                        <div className="pp-faq-cta-btns">
                            <button className="pp-faq-btn-primary" onClick={() => navigate("/request-form")}>
                                Request a Free Quote
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M5 12h14M12 5l7 7-7 7" />
                                </svg>
                            </button>
                            <a href="https://wa.me/919818548369" target="_blank" rel="noreferrer" className="pp-faq-btn-wa">
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51a12.8 12.8 0 0 0-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z" />
                                    <path d="M11.999 2C6.477 2 2 6.477 2 12c0 1.89.525 3.66 1.438 5.168L2 22l4.948-1.42A9.955 9.955 0 0 0 12 22c5.523 0 10-4.477 10-10S17.523 2 12 2z" />
                                </svg>
                                WhatsApp Us
                            </a>
                        </div>
                    </div>
                    {/* Decorative right side */}
                    <div className="pp-faq-cta-deco" aria-hidden="true">
                        <div className="pp-faq-cta-deco-ring pp-faq-cta-deco-ring--1" />
                        <div className="pp-faq-cta-deco-ring pp-faq-cta-deco-ring--2" />
                        <div className="pp-faq-cta-deco-ring pp-faq-cta-deco-ring--3" />
                        <div className="pp-faq-cta-deco-dot" />
                    </div>
                </div>
            </section>

        </main>
    );
};

export default PompomFaq;
